import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client";
import type { Deployment } from "../api/types";
import { t } from "../i18n/i18n";
import { useUi } from "../state/ui";
import {
  bridgesOf,
  dossierWrite,
  recordWrite,
  undoAction,
  useDossierHistory,
  writeContext,
  writeFailure,
} from "./dossierWrites";
import { orderActions, ordersNote } from "./orderSync";
import { useDeployment } from "./units/useDeployment";

/** Pont de la couche du camp : clé d'état (« lat_lon ») et nom. */
export interface BridgeRef {
  key: string;
  name: string;
}

/** Ponts détruits du dossier et bascule détruit / rétabli d'un clic sur le pont (`destroyedBridges` de l'iPad) :
 *  enregistrement dans le dossier (portée du dossier), symbole barré sur la carte, OPO en cours mis à jour, avis
 *  avec annulation par l'historique de la carte (canal « ponts »). */
export function useBridgeState() {
  const queryClient = useQueryClient();
  const exerciseId = useUi((s) => s.exerciseId);
  const notify = useUi((s) => s.notify);
  const requestSave = useUi((s) => s.requestSave);
  const { data: deployment } = useDeployment();
  const destroyed = deployment?.placement?.destroyed_bridges ?? [];
  useDossierHistory();

  const mutation = useMutation({
    mutationFn: ({ bridge, destroy }: { bridge: BridgeRef; destroy: boolean }) =>
      dossierWrite(queryClient, (target, opo) => api.setBridge(target, bridge.key, destroy, opo), {
        notSaved: t("Enregistrez le dossier pour détruire un pont."),
        channel: "ponts",
      }),
    onSuccess: (write, { bridge, destroy }) => {
      const recorded = recordWrite(
        write,
        destroy ? t("destruction du pont {name}", { name: bridge.name }) : t("rétablissement du pont {name}", { name: bridge.name }),
      );
      notify({
        text: destroy ? t("Pont détruit : {name}", { name: bridge.name }) : t("Pont rétabli : {name}", { name: bridge.name }),
        detail:
          ordersNote(write.result, { centac: Boolean(write.result.centac) }) ||
          t("L'état du pont est enregistré dans le dossier."),
        tone: "ok",
        ...orderActions(undoAction(recorded), write.result),
      });
    },
    onError: (error) => writeFailure(queryClient, error, t("État du pont non enregistré")),
  });

  /** Clic sur un pont : détruit s'il est intact, rétabli s'il est détruit. Sans dossier enregistré, le bouton
   *  « Enregistrer » (ou « Créer le dossier ») des Données initiales clignote pour désigner l'action attendue. */
  const toggle = (bridge: BridgeRef) => {
    // État relu à l'appel (et non au rendu) : le dossier vient peut-être d'être enregistré.
    if (!useUi.getState().deploymentParams?.exercise_id) {
      requestSave();
      const action = t("détruire un pont");
      notify({
        text: exerciseId
          ? t("Enregistrez les modifications du dossier pour {action}", { action })
          : t("Créez le dossier pour {action}", { action }),
        detail: t("L'état des ponts est mémorisé dans le dossier d'exercice."),
        tone: "info",
      });
      return;
    }
    // État du pont relu dans le cache (une annulation a pu le changer depuis le rendu).
    const current = queryClient.getQueryData<Deployment>(writeContext().key);
    mutation.mutate({ bridge, destroy: !(current ? bridgesOf(current) : destroyed).includes(bridge.key) });
  };

  return { destroyed, toggle, busy: mutation.isPending };
}

